import { Fragment, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ClipboardSignature, Image, Plus, Trash2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import Loader from '@/components/Loader';
import { displayFullName, formatHeightForInput, genUniqueId } from '@/lib/utils';
import { useSearchParams } from 'react-router-dom';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { FormFieldTimePicker } from '@/components/FormFieldTimePicker';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import { OneLineText } from '@/components/OneLineText';
import {
  ATTEMPT_TYPE,
  MAX_IMAGE_LENGTH_ATTEMPT,
  type AttemptDetails,
  type ImageType,
  type ServiceFields,
  type WorkOrderAttempt,
} from '@/types/workOrderAttempt.types';
import { NAME_VALIDATION } from '@/utils/constant';
import { ImageDisplayDialog } from '@pages/caseManagement/processDetails/ImageDisplayDialog';
import { VendorResponse } from '@/types/vendor.types';

export type Attempt = {
  id: string;
  name: string;
  data: AttemptDetails;
};

export const WorkOrderAttemptsForm = ({
  isFetching = false,
  existingAttempts,
  mode,
  workOrder,
}: {
  mode: string | null;
  isFetching?: boolean;
  existingAttempts?: WorkOrderAttempt[];
  workOrder: {
    server:
      | {
          userId: string;
          firstName: string;
          lastName: string;
        }
      | undefined;
    vendor: VendorResponse | undefined;
  };
}) => {
  const [searchParam] = useSearchParams();
  const { getValues, watch, clearErrors, control, setValue, trigger } = useFormContext();

  const [attempts, setAttempts] = useState<Attempt[]>([
    { id: genUniqueId(), name: 'New Attempt', data: {} as AttemptDetails },
  ]);
  const [activeAttemptId, setActiveAttemptId] = useState<string>(() => attempts[0]?.id ?? '');
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [displayPreview, setDisplayPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageType | null>(null);

  const currentAttemptIndex = attempts.findIndex((attempt) => attempt.id === activeAttemptId);
  const type = watch(`attempts[${currentAttemptIndex}].type`) as ATTEMPT_TYPE;
  const images = watch(`attempts[${currentAttemptIndex}].images`) as ImageType[];
  const attemptId = searchParam.get('attemptId');
  const disabledStyle = 'disabled:opacity-100 text-black';

  const handleAttemptSwitch = (id: string) => {
    setActiveAttemptId(id);
  };

  const getDefaultServerName = () =>
    workOrder?.server ? displayFullName(workOrder.server) : workOrder?.vendor ? workOrder?.vendor.name : '';

  const setDefaultServerName = (index: number) => {
    setValue(`attempts[${index}].serverName`, getDefaultServerName());
  };

  const addAttempt = () => {
    const newAttemptId = genUniqueId();
    setAttempts([
      ...attempts,
      { id: newAttemptId, name: `Attempt ${attempts.length + 1}`, data: {} as AttemptDetails },
    ]);
    setActiveAttemptId(newAttemptId);
    setDefaultServerName(attempts.length);
  };

  const deleteAttempt = (id: string) => {
    const index = attempts.findIndex((attempt) => attempt.id === id);
    setDeleteIndex(index);
    const updatedAttempts = attempts
      .filter((attempt) => attempt.id !== id)
      .map((attempt, index) => {
        return {
          ...attempt,
          name: attempt.data.attemptId ? attempt.name : `Attempt ${index + 1}`,
        };
      });

    const updatedValues = (getValues('attempts') as AttemptDetails[]).filter((_, i: number) => i !== index);

    setAttempts(updatedAttempts);

    if (updatedAttempts.length > 0) {
      const nextAttemptIndex = index < updatedAttempts.length ? index + 1 : updatedAttempts.length - 1;
      setActiveAttemptId(updatedAttempts[nextAttemptIndex - 1].id);
    } else {
      setActiveAttemptId('');
    }
    clearErrors(`attempts[${index}]`);

    setValue('attempts', updatedValues, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const resetServiceFields = (currentAttemptIndex: number) => {
    (
      Object.keys({
        personAccepting: null,
        relation: null,
        age: null,
        height: null,
        weight: null,
        hair: null,
        sex: null,
        race: null,
        otherDetails: null,
      }) as (keyof ServiceFields)[]
    ).forEach((key) => {
      setValue(`attempts[${currentAttemptIndex}].${key}`, null);
    });
  };

  useEffect(() => {
    const attempts = existingAttempts;
    if (!attempts) return;
    const newAttempts: Attempt[] = attempts.map((details, index) => ({
      id: genUniqueId(),
      name: `Attempt ${index + 1}`,
      data: {
        ...details,
        height: formatHeightForInput(details.height),
        images: details.workOrderAttemptMedia?.map((media) => ({
          id: genUniqueId(),
          name: media.media?.metadata.fileName ?? 'Image',
          image: null,
          mediaId: media.mediaId,
          mediaData: media.media,
        })),
      },
    }));

    if (searchParam.get('attemptMode') === 'create') {
      if (newAttempts.length) {
        newAttempts.push({
          id: genUniqueId(),
          name: `Attempt ${newAttempts.length + 1}`,
          data: {} as AttemptDetails,
        });
        setDefaultServerName(newAttempts.length - 1);
      } else {
        const firstAttempt: Attempt = {
          id: genUniqueId(),
          name: 'Attempt 1',
          data: {} as AttemptDetails,
        };
        setAttempts([firstAttempt]);
        setActiveAttemptId(firstAttempt.id);
        setDefaultServerName(0);

        return;
      }
    }
    setAttempts(newAttempts);

    setActiveAttemptId(
      newAttempts.find((attempt) => attempt.data.attemptId === attemptId)?.id ?? newAttempts[newAttempts.length - 1]?.id
    );
  }, [existingAttempts]);

  return (
    <Fragment key={activeAttemptId}>
      {(() => {
        const currentAttemptIndex = attempts.findIndex((person) => person.id === activeAttemptId);

        if (currentAttemptIndex === -1) {
          let newIndex = attempts.length - 1;
          if (deleteIndex) {
            if (deleteIndex === 0) {
              newIndex = 1;
            } else {
              newIndex = deleteIndex - 1;
            }
          }
          setActiveAttemptId(attempts[newIndex].id);
          return null;
        }
        return (
          <div className="flex flex-col gap-8 min-h-[calc(100vh-45vh)] ">
            <Loader isLoading={isFetching} className="h-full flex min-h-[38vh]">
              <div className="bg-[#F5F5F5] h-12 flex overflow-scroll scrollbar-hide border-b-[1px] border-bordercolor">
                {attempts.map((attempt) => (
                  <div
                    key={attempt.id}
                    onClick={() => handleAttemptSwitch(attempt.id)}
                    className={`p-2 border-r-[1px] border-bordercolor inline-flex flex-row items-center shrink-0 ${
                      attempts.length > 1 ? 'justify-between' : 'justify-start'
                    } gap-2 rounded-none whitespace-nowrap ${
                      activeAttemptId === attempt.id
                        ? 'bg-[#EDF5FF] text-primary'
                        : 'bg-[#E2E2E2] text-primary cursor-pointer'
                    }`}
                  >
                    <div>
                      <ClipboardSignature className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">{attempt.name}</span>
                    {attempts.length > 1 && activeAttemptId === attempt.id && !attempt.data.attemptId && (
                      <div className="flex align-bottom">
                        <Trash2
                          color="#D72222"
                          className="w-4 h-4 cursor-pointer"
                          strokeWidth="1"
                          onClick={() => deleteAttempt(activeAttemptId)}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {(!!workOrder.server || !!workOrder.vendor) && mode !== 'view' && (
                  <Button
                    onClick={addAttempt}
                    className="p-2 bg-primary h-12 w-12 text-white flex justify-center items-center rounded-none"
                  >
                    <Plus />
                  </Button>
                )}
              </div>

              {attempts.length > 0 ? (
                <div className="flex flex-col gap-4 w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormFieldCalendar
                      name={`attempts[${currentAttemptIndex}].dateOfAttempt`}
                      control={control}
                      label="Date of Attempt"
                      placeholder="Select Date"
                      required
                      disabledStyle={disabledStyle}
                      disabled={mode === 'view'}
                      disabledAt={(date) => date > new Date()}
                    />
                    <FormFieldTimePicker
                      name={`attempts[${currentAttemptIndex}].timeOfAttempt`}
                      label="Time of Attempt"
                      required
                      use12HourFormat
                      disabledStyle={disabledStyle}
                      disabled={mode === 'view'}
                      placeHolder="Select Time"
                      timeFields={['H', 'M']}
                    />
                  </div>
                  <FormFieldComponent
                    name={`attempts[${currentAttemptIndex}].serverName`}
                    control={control}
                    label="Server"
                    disabledStyle={mode === 'view' ? disabledStyle : undefined}
                    disabled
                    defaultValue={getDefaultServerName()}
                  />
                  <FormFieldComponent
                    name={`attempts[${currentAttemptIndex}].additionalInformation`}
                    control={control}
                    label="Additional Information"
                    disabledStyle={disabledStyle}
                    placeholder="Enter Additional Information"
                    disabled={mode === 'view'}
                  />
                  <FormFieldComponent
                    name={`attempts[${currentAttemptIndex}].description`}
                    control={control}
                    label="Description of Service"
                    disabledStyle={disabledStyle}
                    placeholder="Enter Description of Service"
                    disabled={mode === 'view'}
                  />

                  <div className="flex flex-col gap-2">
                    {mode !== 'view' && (
                      <div className="flex flex-col gap-2">
                        <FormFieldComponent
                          name={`attempts[${currentAttemptIndex}].images`}
                          control={control}
                          type={'multipleFile'}
                          defaultFileName="Click to upload image"
                          acceptedFileTypes={['jpeg', 'png', 'heif', 'heic', 'raw', 'jpg']}
                          disabled={images?.length >= MAX_IMAGE_LENGTH_ATTEMPT || mode === 'view'}
                          onChange={(e) => {
                            if (!e.target.files || e.target.files.length === 0) return;
                            const newFiles: ImageType[] = Array.from(e.target.files).map((file) => ({
                              id: genUniqueId(),
                              name: file.name,
                              image: file,
                            }));
                            const currentImages: ImageType[] =
                              (getValues(`attempts[${currentAttemptIndex}].images`) as ImageType[]) || [];
                            setValue(`attempts[${currentAttemptIndex}].images`, [...currentImages, ...newFiles], {
                              shouldDirty: true,
                              shouldValidate: true,
                              shouldTouch: true,
                            });
                          }}
                        />
                        <div className="flex justify-end w-full mr-10 text-[13px]">{`${images?.length || 0} of 50`}</div>
                      </div>
                    )}

                    {displayPreview && (
                      <ImageDisplayDialog
                        attachments={getValues(`attempts[${currentAttemptIndex}].images`) as ImageType[]}
                        onClose={() => setDisplayPreview(false)}
                        attachmentId={previewImage?.id ?? ''}
                      />
                    )}
                    <div className="grid grid-cols-2 gap-6 md:w-[80%] py-2">
                      {images?.map((img) => (
                        <div
                          key={img.id}
                          className="flex justify-between h-[56px] bg-[#F5F5F5] rounded-md items-center p-4 text-primary"
                        >
                          <div
                            className="flex gap-2 items-center cursor-pointer"
                            onClick={() => {
                              setPreviewImage(img);
                              setDisplayPreview(true);
                            }}
                          >
                            <div>
                              <Image className="h-5 w-5 text-primary" />
                            </div>
                            <OneLineText text={img.name} className="flex mr-5" />
                          </div>
                          {mode !== 'view' && (
                            <div>
                              <Trash2
                                onClick={() => {
                                  const updatedImages = images.filter((i) => i.id !== img.id);

                                  setValue(`attempts[${currentAttemptIndex}].images`, updatedImages, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                    shouldTouch: true,
                                  });
                                }}
                                className="cursor-pointer text-error h-5 w-5"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="w-full gap-0 flex flex-col rounded-md border-bordercolor border-[1px] ">
                    <div className="rounded-t-md font-semibold bg-[#F5F5F5] px-4 py-4">
                      <span>Service Information</span>
                    </div>

                    <div className="rounded-b-md flex flex-col px-4 py-4 gap-6">
                      <div>
                        <FormFieldRadioGroup
                          label="Type"
                          direction="horizontal"
                          disabled={mode === 'view'}
                          name={`attempts[${currentAttemptIndex}].type`}
                          options={[
                            { value: ATTEMPT_TYPE.PERSONAL, label: 'Personal' },
                            { value: ATTEMPT_TYPE.SUB, label: 'Sub' },
                            { value: ATTEMPT_TYPE.BY_MAIL, label: 'Served by Mail' },
                            { value: ATTEMPT_TYPE.BY_EMAIL, label: 'Served by E-mail' },
                            { value: ATTEMPT_TYPE.ATTEMPTED, label: 'Attempted' },
                            { value: ATTEMPT_TYPE.NOT_SERVED, label: 'Not Served' },
                            { value: ATTEMPT_TYPE.POSTED, label: 'Posted' },
                          ]}
                          required
                          onChange={() => {
                            resetServiceFields(currentAttemptIndex);
                            trigger(`attempts[${currentAttemptIndex}]`);
                          }}
                        />
                      </div>
                      {(type === ATTEMPT_TYPE.PERSONAL || type === ATTEMPT_TYPE.SUB) && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].personAccepting`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Person Accepting"
                              placeholder="Enter Person Accepting"
                              disabledStyle={disabledStyle}
                              {...NAME_VALIDATION}
                              required
                            />
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].relation`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Title/ Relation to Serve"
                              placeholder="Enter Title/ Relation to Servee"
                              capitalizeFirstLetter={true}
                              allowSpecialCharacters={false}
                              disabledStyle={disabledStyle}
                              allowNumber={false}
                              required
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].age`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Age"
                              disabledStyle={disabledStyle}
                              placeholder="Enter Age"
                              type="number"
                              maxDecimalPlaces={0}
                              allowDecimals={false}
                              capitalizeFirstLetter={true}
                              required
                            />
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].height`}
                              disabled={mode === 'view'}
                              control={control}
                              disabledStyle={disabledStyle}
                              label="Height (feet and inch)"
                              placeholder="Enter Height"
                              onChange={(e) => {
                                let value = e.target.value;
                                value = value.replace(/[^0-9'"]/g, '');
                                if (value.includes("'")) {
                                  const parts = value.split("'");
                                  if (parts.length > 2) {
                                    value = parts[0] + "'" + parts.slice(1).join('');
                                  }
                                  if (parts[1] && parts[1].length > 2) {
                                    value = parts[0] + "'" + parts[1].substring(0, 2);
                                  }
                                }
                                if (value.includes('"')) {
                                  const parts = value.split('"');
                                  if (parts.length > 2) {
                                    value = parts[0] + '"' + parts.slice(1).join('');
                                  }
                                  if (parts[1] && parts[1].length > 2) {
                                    value = parts[0] + '"' + parts[1].substring(0, 2);
                                  }
                                }
                                e.target.value = value;
                              }}
                              required
                            />
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].weight`}
                              disabledStyle={disabledStyle}
                              disabled={mode === 'view'}
                              control={control}
                              label="Weight(lbs)"
                              placeholder="Enter Weight(lbs)"
                              type="number"
                              required
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].hair`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Hair Color"
                              disabledStyle={disabledStyle}
                              placeholder="Enter Hair Color"
                              replaceRegex={/[^a-zA-Z]/g}
                              capitalizeFirstLetter={true}
                              required
                            />
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].sex`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Sex"
                              disabledStyle={disabledStyle}
                              placeholder="Enter Sex"
                              replaceRegex={/[^a-zA-Z]/g}
                              capitalizeFirstLetter={true}
                              required
                            />
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].race`}
                              disabled={mode === 'view'}
                              disabledStyle={disabledStyle}
                              control={control}
                              label="Race"
                              placeholder="Enter Race"
                              replaceRegex={/[^a-zA-Z]/g}
                              capitalizeFirstLetter={true}
                              required
                            />
                          </div>
                          <div>
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].otherDetails`}
                              disabledStyle={disabledStyle}
                              control={control}
                              disabled={mode === 'view'}
                              label="Other"
                              placeholder="Enter"
                              capitalizeFirstLetter={true}
                            />
                          </div>
                        </>
                      )}
                      {type === ATTEMPT_TYPE.BY_EMAIL && (
                        <>
                          <div className="grid grid-cols-1 gap-6">
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].emailAddress`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Email Address"
                              placeholder="Enter Email Address"
                              disabledStyle={disabledStyle}
                              required
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormFieldCalendar
                              name={`attempts[${currentAttemptIndex}].emailSentDate`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Email Sent Date"
                              placeholder="Select Date"
                              disabledStyle={disabledStyle}
                            />
                            <FormFieldCalendar
                              name={`attempts[${currentAttemptIndex}].emailAcknowledgementDate`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Email Acknowledgement Date"
                              placeholder="Select Date"
                              disabledStyle={disabledStyle}
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-6">
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].remarks`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Email Remarks"
                              placeholder="Enter Remarks for 'Affidavit of Service by Electronic Mail'"
                              disabledStyle={disabledStyle}
                            />
                          </div>
                        </>
                      )}
                      {type === ATTEMPT_TYPE.BY_MAIL && (
                        <>
                          <div className="grid grid-cols-1 gap-6">
                            <FormFieldComponent
                              name={`attempts[${currentAttemptIndex}].trackingNumber`}
                              disabled={mode === 'view'}
                              control={control}
                              label="Certified Mail Number"
                              placeholder="Enter Certified Mail Number"
                              disabledStyle={disabledStyle}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div>No Attempts Available</div>
              )}
            </Loader>
          </div>
        );
      })()}
    </Fragment>
  );
};
